import React, { useState, useEffect } from "react";
import {MenuItem, TextField, Autocomplete } from "@mui/material";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from "reactstrap";
import { useTranslation } from "context/TranslationProvider";
import CountriesCodes from "assets/data/country_phone.json";
import helpers from "utils/helpers";

const UpdateUserModal = ({ user, open, onClose, onSave }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    phoneCountryCode: null,
    homeAddress: "",
    country: null,
    uploadedIdNumber: "",
    uploadedIdType: "",
  });

  const [emailError, setEmailError] = useState(""); 
  useEffect(() => {
    if (user) {
      const selectedCountry =
        CountriesCodes.find(
          (c) =>c.label === user.country ||
                c.code === user.country ||
                c.label === user.country?.label ||
                c.code === user.country?.code
        ) || null;

        //Find phone country code separately
      const sortedByLongestPhone = [...CountriesCodes].sort(
        (a, b) => (b.phone?.length || 0) - (a.phone?.length || 0)
      );

      const normalizedUserPhone = (user.phoneNumber || "").replace(/[\s-]/g, "");

      const selectedPhoneCountry =
        sortedByLongestPhone.find((c) => {
          const normalizedDialCode = (c.phone || "").replace(/[\s-]/g, "");
          return normalizedDialCode && normalizedUserPhone.startsWith(normalizedDialCode);
        }) || null;

      let rawPhone = user.phoneNumber || "";
      if (selectedPhoneCountry?.phone) {
        const normalizedDialCode = selectedPhoneCountry.phone.replace(/[\s-]/g, "");
        const normalizedRaw = rawPhone.replace(/[\s-]/g, "");
        if (normalizedRaw.startsWith(normalizedDialCode)) {
          rawPhone = normalizedRaw.slice(normalizedDialCode.length).trim();
        }
      }
      setFormData({
        name: user.name || "",
        email: user.email || "",
        phoneNumber: rawPhone,
        phoneCountryCode: selectedPhoneCountry,
        homeAddress: user.homeAddress || "",
        country: selectedCountry,
        uploadedIdNumber: user.uploadedIdNumber || "",
        uploadedIdType: helpers.normalizeUploadedIdType(user.uploadedIdType),
      });
      setEmailError("");
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "email") setEmailError("");

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);

  if (formData.email && !isEmailValid) {
    setEmailError(
      t("modal.verification.invalidEmail", "Please enter a valid email address")
    );
    return;
  }
  
  const dialCode = formData.phoneCountryCode?.phone || "";
  let phone = formData.phoneNumber;
  if (dialCode && !phone.startsWith(dialCode)) {
    phone = `${dialCode} ${phone}`;
  }

  try {
    setLoading(true);
    const res = await onSave({
      ...formData,
      phoneNumber: phone,
      country: formData.country,
      uploadedIdType: formData.uploadedIdType || "",
      uploadedIdNumber: formData.uploadedIdNumber || "",
    });

    // Handle ALL backend errors
    if (res?.error) {
      if (res.error === "auth.signup.emailAlreadyUsed") {
        setEmailError(t("auth.signup.emailAlreadyUsed", "This email is already registered. Please use a different email."));
      } else {
        // generic fallback error
        setEmailError(
          t(res.error, t("toast.unexpectedError", "Unexpected error occurred."))
        );
      }
      return;
    }
//Success case
    setEmailError("");
    onClose();

  } catch (err) {
    const backendError = err?.response?.data?.error;

    if (backendError === "auth.signup.emailAlreadyUsed") {
        setEmailError(t("auth.signup.emailAlreadyUsed", "This email is already registered. Please use a different email."));
      } else {
        setEmailError(t(backendError || "toast.unexpectedError", "Something went wrong. Please try again."));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={open} toggle={onClose} centered>
      <ModalHeader toggle={onClose} className="border">
        {t("modal.updateUser.title", "Update User")}
      </ModalHeader>

      <ModalBody>
        <TextField
          label={t("table.headers.name", "Name")}
          name="name"
          fullWidth
          size="small"
          margin="dense"
          value={formData.name}
          onChange={handleChange}
        />

        <TextField
          label={t("table.headers.email", "Email")}
          name="email"
          fullWidth
          size="small"
          margin="dense"
          value={formData.email}
          onChange={handleChange}
          error={!!emailError}
          helperText={emailError}
        />

        <div className="d-flex align-items-start">
          <Autocomplete
            disablePortal
            options={CountriesCodes}
            value={formData.phoneCountryCode}
            onChange={(e, newValue) =>
              setFormData((prev) => ({ ...prev, phoneCountryCode: newValue }))
            }
            getOptionLabel={(option) =>
              option && option.phone ? `+${option.phone}` : ""
            }
            isOptionEqualToValue={(option, value) => option?.code === value?.code}
            className="mr-2"
            renderOption={(props, option) => (
              <li {...props} key={option.code}>
                <img
                  width="20"
                  className="mr-2"
                  src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                  alt=""
                />
                +{option.phone}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("auth.signup.countryCode", "Code")}
                size="small"
                margin="dense"
                fullWidth
              />
            )}
          />

          <TextField
            label={t("table.headers.phone", "Phone")}
            name="phoneNumber"
            fullWidth
            size="small"
            margin="dense"
            value={formData.phoneNumber}
            onChange={handleChange}
          />
        </div>
        <TextField
          label={t("page.user-profile.form.homeAddress", "Home Address")}
          name="homeAddress"
          fullWidth
          size="small"
          margin="dense"
          value={formData.homeAddress}
          onChange={handleChange}
        />

       <Autocomplete
  disablePortal
  options={CountriesCodes}
  value={formData.country}
  onChange={(e, newValue) =>
    setFormData((prev) => ({ ...prev, country: newValue }))
  }
  getOptionLabel={(option) => option?.label || ""}
 isOptionEqualToValue={(option, value) => option?.code === value?.code}
  renderOption={(props, option) => (
    <li {...props} key={option.code}>
      <img
        width="20"
        className="mr-2"
        src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
        alt=""
      />
      {option.label}
    </li>
  )}
  renderInput={(params) => (
    <TextField
      {...params}
      label={t("page.user-profile.form.country", "Country")}
      size="small"
      margin="dense"
      fullWidth
    />
  )}
/>
      <TextField
        label={t("page.user-profile.form.uploadId", "Upload ID")}
        name="uploadedIdType"
        select
        fullWidth
        size="small"
        margin="dense"
        value={formData.uploadedIdType}
        onChange={handleChange}
      >
        {Object.values(helpers.USER_ID_TYPES).map((type) => (
          <MenuItem key={type} value={type}>
            {helpers.getUserIdTypeLabel(type, t)}
          </MenuItem>
        ))}
      </TextField>

        <TextField
        label={t("page.user-profile.form.idNumber", "ID Number")}
        name="uploadedIdNumber"
        fullWidth
        size="small"
        margin="dense"
        value={formData.uploadedIdNumber}
        onChange={handleChange}
      />
      </ModalBody>

      <ModalFooter className="border">
        <Button color="secondary" onClick={onClose}>
          {t("common.cancel", "Cancel")}
        </Button>

        <Button color="warning" onClick={handleSubmit} disabled={loading}>
          {loading ? (
            <>
              <Spinner size="sm" className="mr-2" />
              {t("button.save", "Save")}
            </>
          ) : (
            t("button.save", "Save")
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default UpdateUserModal;
